import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, PowerOff, RefreshCw } from 'lucide-react';
import { InventoryItemType, StockMovementType } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { INVENTORY_TABS } from '@/Pages/tabRoutes';
import {
  apiMessage,
  formatDateTime,
  formatQuantity,
  useBranches,
  useQueryParams,
} from './shared';
import { WarehouseDialog, WarehousesPanel, type WarehouseTarget } from './components/WarehousesPanel';
import { UnitDialog, UnitsPanel, type UnitTarget } from './components/UnitsPanel';
import {
  InventoryItemDialog,
  type InventoryItemRow,
  type ItemTarget,
} from './components/InventoryItemDialog';
import type { UnitRow, WarehouseRow } from './components/inventoryDefs';
import { StockCountSheet } from './components/StockCountSheet';

type Operation = 'receive' | 'issue' | 'waste' | 'transfer' | 'adjust';

const ITEM_TYPES: InventoryItemType[] = Object.values(InventoryItemType);

const MOVEMENT_TYPES: StockMovementType[] = Object.values(StockMovementType);

/** §21 — quantities are (12,3) and costs (12,4); they travel as strings so a
 *  JSON double never rounds them before `decimal.js` sees them. */
const DECIMAL = /^-?\d+(\.\d+)?$/;

interface Level {
  inventoryItemId: string;
  inventoryItem: { id: string; name: string; sku: string | null } | null;
  warehouseId: string;
  warehouse: { id: string; name: string; code: string } | null;
  unit: { id: string; code: string } | null;
  onHand: string;
  value: string;
}

interface Movement {
  id: string;
  type: string;
  quantity: string;
  unitCost: string;
  totalCost: string;
  reason: string | null;
  occurredAt: string;
  inventoryItem: { id: string; name: string; sku: string | null } | null;
  warehouse: { id: string; name: string; code: string } | null;
  unit: { id: string; code: string } | null;
}

interface LowStockRow {
  inventoryItemId: string;
  name: string;
  sku: string | null;
  unit: { id: string; code: string } | null;
  onHand: string;
  reorderPoint: string;
  shortfall: string;
  suggestedOrderQuantity: string;
  isOutOfStock: boolean;
}

interface VarianceRow {
  movementId: string;
  occurredAt: string;
  inventoryItem: { id: string; name: string; sku: string | null } | null;
  warehouse: { id: string; name: string; code: string } | null;
  countedBy: { id: string; firstName: string; lastName: string | null } | null;
  counted: string;
  expected: string;
  variance: string;
  varianceValue: string;
  reason: string | null;
}

/** §21 gross margin per catalog item. `costedOrderItems` is the honesty column:
 *  a line with no stock movement contributes revenue but no cost, so a margin
 *  computed over uncosted lines reads far too healthy. */
interface MarginRow {
  catalogItemId: string;
  name: string;
  quantitySold: string;
  revenue: string;
  cost: string;
  margin: string;
  marginPercent: string | null;
  orderItems: number;
  costedOrderItems: number;
}

interface MarginReport {
  from: string;
  to: string;
  branchId: string | null;
  totals: { revenue: string; cost: string; margin: string; marginPercent: string | null };
  items: MarginRow[];
}

interface Valuation {
  warehouseId: string | null;
  lines: number;
  totalValue: string;
}

interface OperationForm {
  inventoryItemId: string;
  quantity: string;
  unitId: string;
  warehouseId: string;
  toWarehouseId: string;
  unitCost: string;
  reason: string;
}

const EMPTY_FORM: OperationForm = {
  inventoryItemId: '',
  quantity: '',
  unitId: '',
  warehouseId: '',
  toWarehouseId: '',
  unitCost: '',
  reason: '',
};

/**
 * P11-02 → P11-06 — inventory.
 *
 * Stock on hand is never stored: every number here is a fold of the movement
 * ledger, which is why a count posts the *difference* rather than overwriting a
 * balance. Every quantity leaves this screen as a decimal string.
 */
export default function Inventory() {
  const { t, i18n } = useTranslation();
  // D-04 — stock is valued in the branch's own money. `formatAmount` printed
  // these figures with no currency at all; `useCurrency().format` carries the
  // code and the right number of minor-unit digits.
  const { format: money, code: currencyCode } = useCurrency();
  const { can } = usePermission();
  const { confirm } = useConfirm();

  const routed = useRoutedTabs('/inventory', INVENTORY_TABS);
  const { select } = routed;
  const tab = routed.open ? routed.current : null;
  const [search, setSearch] = useState('');
  const [itemType, setItemType] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [movementType, setMovementType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const mayAdjust = can('inventory.adjust');
  const mayTransfer = can('inventory.transfer');
  const [operation, setOperation] = useState<Operation | null>(null);
  const [form, setForm] = useState<OperationForm>(EMPTY_FORM);
  /** The count is a whole sheet, not a dialog: it compares every item on the
   *  shelf against the ledger, which does not fit beside a modal's scrollbar. */
  const [counting, setCounting] = useState(false);
  const [busy, setBusy] = useState(false);

  /** X-16 — the definition forms. `'new'` creates, a row edits. */
  const [warehouseTarget, setWarehouseTarget] = useState<WarehouseTarget | null>(null);
  const [unitTarget, setUnitTarget] = useState<UnitTarget | null>(null);
  const [itemTarget, setItemTarget] = useState<ItemTarget | null>(null);
  /** Raised when a transfer is asked for and there is nowhere to move stock to. */
  const [transferDeadEnd, setTransferDeadEnd] = useState(false);
  const branches = useBranches();

  // ------------------------------------------------------------------ data

  const warehouses = useApi<unknown>('/inventory/warehouses', { limit: 200 });
  const warehouseRows = rowsOf<WarehouseRow>(warehouses.data).rows;

  const units = useApi<unknown>('/inventory/units', { limit: 200 });
  const unitRows = rowsOf<UnitRow>(units.data).rows;

  const itemParams = useQueryParams({ search, type: itemType, limit: 200 });
  const items = useApi<unknown>('/inventory/items', itemParams);
  const itemRows = rowsOf<InventoryItemRow>(items.data).rows;

  const levelParams = useQueryParams({ warehouseId });
  const levels = useApi<Level[]>('/stock-movements/levels', levelParams);
  const levelRows = rowsOf<Level>(levels.data).rows;

  const movementParams = useQueryParams({
    warehouseId,
    type: movementType,
    from: from ? new Date(`${from}T00:00:00`).toISOString() : '',
    to: to ? new Date(`${to}T23:59:59.999`).toISOString() : '',
    limit: 100,
  });
  const movements = useApi<unknown>(
    tab === 'movements' ? '/stock-movements' : null,
    movementParams,
  );
  const movementRows = rowsOf<Movement>(movements.data).rows;

  const lowStock = useApi<unknown>(
    tab === 'low-stock' ? '/inventory/reports/low-stock' : null,
    useQueryParams({ warehouseId, limit: 100 }),
  );
  const lowStockRows = rowsOf<LowStockRow>(lowStock.data).rows;

  const valuation = useApi<Valuation>(
    tab === 'valuation' ? '/inventory/reports/valuation' : null,
    useQueryParams({ warehouseId }),
  );

  const variance = useApi<unknown>(
    tab === 'variance' ? '/inventory/reports/variance' : null,
    useQueryParams({
      warehouseId,
      from: from ? new Date(`${from}T00:00:00`).toISOString() : '',
      to: to ? new Date(`${to}T23:59:59.999`).toISOString() : '',
      limit: 100,
    }),
  );
  const varianceRows = rowsOf<VarianceRow>(variance.data).rows;

  // Margin is priced per branch, not per warehouse, so it takes the date window
  // and leaves `warehouseId` alone.
  const margin = useApi<MarginReport>(
    tab === 'margin' ? '/inventory/reports/margin' : null,
    useQueryParams({
      from: from ? new Date(`${from}T00:00:00`).toISOString() : '',
      to: to ? new Date(`${to}T23:59:59.999`).toISOString() : '',
    }),
  );

  /**
   * Whether the balances on this screen are known at all.
   *
   * `/stock-movements/levels` is the only source of on-hand quantities here, and
   * the fold below cannot tell "nothing on the shelf" from "the request was
   * refused" — an empty `levelRows` reads identically either way. Left unread,
   * `levels.error` produced the most alarming screen the app can draw and no
   * indication anything had failed: every item at zero, every item with a
   * reorder point flagged red as below it, the valuation table reporting an
   * empty venue. Zero is a fact about stock; a failed read is a fact about the
   * request, and the two must not be printed the same way.
   */
  const stockUnknown = Boolean(levels.error);

  /** Per-warehouse balances, keyed by item, so the Items tab needs one extra call. */
  const stockByItem = useMemo(() => {
    const map = new Map<string, Level[]>();
    for (const level of levelRows) {
      const bucket = map.get(level.inventoryItemId) ?? [];
      bucket.push(level);
      map.set(level.inventoryItemId, bucket);
    }
    return map;
  }, [levelRows]);

  const reloadLedger = () => {
    levels.reload();
    items.reload();
    if (tab === 'movements') movements.reload();
    if (tab === 'low-stock') lowStock.reload();
    if (tab === 'valuation') valuation.reload();
    if (tab === 'variance') variance.reload();
    if (tab === 'margin') margin.reload();
  };

  // ------------------------------------------------------------- operations

  // `WasteStockDto` and `AdjustStockDto` mark `reason` @IsNotEmpty; the other
  // three leave it optional.
  const needsReason = operation === 'waste' || operation === 'adjust';

  /**
   * Why a quantity would be refused. An adjustment is the one *signed*
   * operation — `adjust()` rejects only zero, because a correction has to move
   * the balance in one direction or the other. Everything else is one-way and
   * `StockService` insists it be positive.
   */
  const quantityError = useMemo(() => {
    const typed = form.quantity.trim();
    if (!DECIMAL.test(typed)) return t('operations.inventory.quantityInvalid');
    if (operation === 'adjust') {
      return Number(typed) === 0 ? t('operations.inventory.quantityNonZero') : null;
    }
    return Number(typed) > 0 ? null : t('operations.inventory.quantityPositive');
  }, [form.quantity, operation, t]);

  /**
   * The whole precondition set for the operation dialog, in the order the API
   * checks it. A transfer additionally names its source explicitly: the server
   * compares source against destination *after* resolving an omitted source to
   * the branch default, so a blank source here could still collide.
   */
  const operationError = useMemo(() => {
    if (!operation) return null;
    if (!form.inventoryItemId) return t('operations.inventory.itemRequired');
    if (quantityError) return quantityError;
    if (needsReason && !form.reason.trim()) return t('operations.inventory.reasonRequired');
    if (operation === 'transfer') {
      if (!form.warehouseId) return t('operations.inventory.sourceRequired');
      if (!form.toWarehouseId) return t('operations.inventory.destinationRequired');
      if (form.warehouseId === form.toWarehouseId) {
        return t('operations.inventory.sameWarehouse');
      }
    }
    return null;
  }, [form, needsReason, operation, quantityError, t]);

  /**
   * A move needs somewhere to move to; one location cannot transfer to itself.
   *
   * X-16 — this used to be the end of the road: a disabled button whose tooltip
   * named a precondition the operator had no screen anywhere in the product to
   * satisfy. It still blocks, but tapping it now opens the way out instead of
   * doing nothing.
   *
   * The count is of warehouses, not of *active* warehouses, on purpose:
   * `StockService` resolves a transfer's endpoints through
   * `WarehousesService.assertExists`, which does not look at `isActive`, so a
   * retired store is still a legal destination and pretending otherwise would
   * block a move the server would have accepted.
   */
  const transferBlocked =
    warehouseRows.length < 2 ? t('operations.inventory.needsTwoWarehouses') : null;

  /**
   * Whether an operation that omits a warehouse can land at all.
   *
   * `resolveDefault()` only ever considers `isActive: true` stores and throws
   * "No active warehouse exists for this tenant" when there are none — which is
   * demo-cafe's exact state, one retired `RTWH1`. Every receive, issue, waste
   * and count on this screen fails on that before it reaches a validation the
   * operator could act on, so it is said up front.
   */
  const noActiveWarehouse =
    !warehouses.loading && !warehouses.error && !warehouseRows.some((w) => w.isActive);
  const retiredWarehouses = warehouseRows.filter((w) => !w.isActive);

  const submitOperation = async () => {
    if (!operation || operationError) return;

    setBusy(true);
    try {
      await api.post(`/stock-movements/${operation}`, {
        inventoryItemId: form.inventoryItemId,
        // String on the wire — never a JS number (§21 decimal discipline).
        quantity: form.quantity.trim(),
        unitId: form.unitId || undefined,
        warehouseId: form.warehouseId || undefined,
        ...(operation === 'transfer' ? { toWarehouseId: form.toWarehouseId } : {}),
        ...(operation === 'receive' && form.unitCost.trim()
          ? { unitCost: form.unitCost.trim() }
          : {}),
        ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
      });
      toast.success(t(`operations.inventory.done.${operation}`));
      setOperation(null);
      setForm(EMPTY_FORM);
      reloadLedger();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  /**
   * X-16 — retiring an inventory item.
   *
   * `InventoryService.remove` sets `deletedAt` and `isActive: false` and checks
   * NOTHING first: not stock on hand, not recipes. It will not refuse, so this
   * does not promise a refusal. What it does is show the balance that is about
   * to go out of the list — read exactly, from `/inventory/items/:id/stock` —
   * and name the one consequence that is genuinely surprising:
   *
   * a published recipe that names this item KEEPS working, because
   * `includeVersion` does not filter `deletedAt` and `resolve()` still finds
   * it; but `validateVersionInput` DOES filter it, so saving any new version of
   * that recipe afterwards fails with a bare id the operator cannot place.
   */
  const retireItem = async (item: InventoryItemRow) => {
    let onHand: { warehouse?: { code?: string } | null; onHand: string }[] = [];
    try {
      const { data } = await api.get(`/inventory/items/${item.id}/stock`);
      const payload = (data as { data?: { stock?: typeof onHand } }).data ?? data;
      onHand = (payload as { stock?: typeof onHand }).stock ?? [];
    } catch {
      onHand = [];
    }
    const held = onHand.filter((row) => Number(row.onHand) !== 0);

    const ok = await confirm({
      title: t('operations.inventory.defs.items.retireTitle', { name: item.name }),
      body: held.length
        ? t('operations.inventory.defs.items.retireBodyStock', {
            balances: held
              .map((row) => `${formatQuantity(row.onHand)} ${item.unit?.code ?? ''}`.trim())
              .join(', '),
          })
        : t('operations.inventory.defs.items.retireBody'),
      confirmLabel: t('operations.inventory.defs.items.retire'),
      tone: 'danger',
    });
    if (!ok) return;

    setBusy(true);
    try {
      await api.delete(`/inventory/items/${item.id}`);
      toast.success(t('operations.inventory.defs.items.retired', { name: item.name }));
      items.reload();
      levels.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.inventory.defs.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const itemOptions = (
    <>
      <option value="">{t('operations.filters.choose')}</option>
      {itemRows.map((item) => (
        <option key={item.id} value={item.id}>
          {item.name}
          {item.sku ? ` (${item.sku})` : ''}
        </option>
      ))}
    </>
  );

  // ---------------------------------------------------------------- render

  /** Hoisted because the count sheet takes over the page and this precondition
   *  applies there too — `resolveDefault()` only considers active stores. */
  const warehouseBanner = noActiveWarehouse ? (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-4">
      <p className="text-sm text-amber-900 dark:text-amber-200">
        {t(
          warehouseRows.length
            ? 'operations.inventory.defs.warehouses.noneActive'
            : 'operations.inventory.defs.warehouses.noneAtAll',
        )}
      </p>
      <Button size="sm" onClick={() => select('warehouses')}>
        {t('operations.inventory.defs.warehouses.manage')}
      </Button>
    </div>
  ) : null;

  /**
   * The count is the one operation on this screen that is a page rather than a
   * dialog: it holds every item in a store beside its ledger balance, which is
   * a table, not a form. Cancelling comes straight back to the tabs, and a
   * posted count reloads the ledger the `variance` tab reads.
   */
  if (counting) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">{t('nav.inventory')}</h1>
        {warehouseBanner}
        <StockCountSheet
          warehouses={warehouseRows}
          initialWarehouseId={warehouseId}
          onClose={() => setCounting(false)}
          onPosted={() => {
            setCounting(false);
            reloadLedger();
          }}
        />
      </div>
    );
  }

  if (!routed.open) return routed.redirect;
  const { tabs, current } = routed;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('nav.inventory')}</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={reloadLedger}>
            <RefreshCw className="h-4 w-4" />
            {t('operations.common.refresh')}
          </Button>

          {mayAdjust &&
            (['receive', 'issue', 'waste', 'adjust'] as const).map((op) => (
              <Button
                key={op}
                size="sm"
                variant={op === 'receive' ? 'default' : 'outline'}
                onClick={() => {
                  setForm({ ...EMPTY_FORM, warehouseId });
                  setOperation(op);
                }}
              >
                {t(`operations.inventory.actions.${op}`)}
              </Button>
            ))}

          {/* `POST /stock-movements/transfer` is the one operation gated on
              `inventory.transfer` rather than `inventory.adjust`. */}
          {mayTransfer && (
            <Button
              size="sm"
              variant="outline"
              title={transferBlocked ?? undefined}
              onClick={() => {
                if (transferBlocked) {
                  setTransferDeadEnd(true);
                  return;
                }
                setForm({ ...EMPTY_FORM, warehouseId });
                setOperation('transfer');
              }}
            >
              {t('operations.inventory.actions.transfer')}
            </Button>
          )}

          {mayAdjust && (
            <Button size="sm" variant="outline" onClick={() => setCounting(true)}>
              {t('operations.inventory.actions.count')}
            </Button>
          )}

          {/* X-16 — defining an item, which no client could do until now. */}
          {mayAdjust && (
            <Button size="sm" onClick={() => setItemTarget('new')}>
              <Plus className="h-4 w-4" />
              {t('operations.inventory.defs.items.new')}
            </Button>
          )}
        </div>
      </div>

      <Tabs
        tabs={tabs.map((entry) => ({ key: entry.id, label: t(entry.labelKey) }))}
        value={current}
        onChange={select}
        label={t('nav.inventory')}
        idPrefix="inventory"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {warehouseBanner}

      {/* Said once, on the two tabs that read balances, rather than left for
          the reader to infer from a screen full of zeros. */}
      {stockUnknown && (tab === 'items' || tab === 'valuation') && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-300 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/50">
          <div>
            <p className="text-sm font-medium text-red-900 dark:text-red-200">
              {t('operations.inventory.stockUnknown')}
            </p>
            <p className="text-xs text-red-800/80 dark:text-red-300/80">{levels.error}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => levels.reload()}>
            {t('common.retry')}
          </Button>
        </div>
      )}

      {tab !== 'warehouses' && (
      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          {tab === 'items' && (
            <>
              <Field label={t('operations.filters.search')}>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} />
              </Field>
              <Field label={t('operations.inventory.itemType')}>
                <Select value={itemType} onChange={(e) => setItemType(e.target.value)}>
                  <option value="">{t('operations.filters.any')}</option>
                  {ITEM_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(`operations.inventory.itemTypes.${type}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}

          <Field label={t('operations.inventory.warehouse')}>
            <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">{t('operations.inventory.allWarehouses')}</option>
              {warehouseRows.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>
          </Field>

          {tab === 'movements' && (
            <Field label={t('operations.inventory.movementType')}>
              <Select value={movementType} onChange={(e) => setMovementType(e.target.value)}>
                <option value="">{t('operations.filters.any')}</option>
                {MOVEMENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`operations.inventory.movementTypes.${type}`)}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {(tab === 'movements' || tab === 'variance') && (
            <>
              <Field label={t('operations.filters.from')}>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Field>
              <Field label={t('operations.filters.to')}>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </Field>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* ---------------------------------------------------------- units */}
      {tab === 'units' && (
        <UnitsPanel
          units={unitRows}
          loading={units.loading}
          error={units.error}
          onRetry={units.reload}
          mayWrite={mayAdjust}
          onEdit={setUnitTarget}
          onChanged={units.reload}
        />
      )}

      {/* ----------------------------------------------------- warehouses */}
      {tab === 'warehouses' && (
        <WarehousesPanel
          warehouses={warehouseRows}
          loading={warehouses.loading}
          error={warehouses.error}
          onRetry={warehouses.reload}
          mayWrite={mayAdjust}
          onEdit={setWarehouseTarget}
          onChanged={warehouses.reload}
        />
      )}

      {/* ---------------------------------------------------------- items */}
      {tab === 'items' &&
        (items.error ? (
          <ErrorState message={items.error} onRetry={items.reload} />
        ) : items.loading && !itemRows.length ? (
          <LoadingState label={t('common.loading')} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('operations.inventory.item')}</TH>
                  <TH>{t('operations.inventory.itemType')}</TH>
                  <TH>{t('operations.inventory.unit')}</TH>
                  <TH>{t('operations.inventory.onHand')}</TH>
                  <TH>{t('operations.inventory.byWarehouse')}</TH>
                  <TH className="text-end">{t('operations.inventory.costPrice')}</TH>
                  {mayAdjust && <TH className="text-end">{t('operations.common.actions')}</TH>}
                </TR>
              </THead>
              <tbody>
                {!itemRows.length ? (
                  <TableEmpty colSpan={mayAdjust ? 7 : 6}>
                    {t('operations.inventory.noItems')}
                  </TableEmpty>
                ) : (
                  itemRows.map((item) => {
                    const stock = stockUnknown ? [] : (stockByItem.get(item.id) ?? []);
                    const onHand = stock.reduce((sum, row) => sum + Number(row.onHand || 0), 0);
                    const belowReorder =
                      !stockUnknown &&
                      item.reorderPoint != null &&
                      onHand <= Number(item.reorderPoint);

                    return (
                      <TR key={item.id}>
                        <TD>
                          <div className="font-medium">{item.name}</div>
                          {item.sku && (
                            <div className="text-xs text-ink-muted">{item.sku}</div>
                          )}
                        </TD>
                        <TD>{t(`operations.inventory.itemTypes.${item.type}`)}</TD>
                        <TD>{item.unit?.code ?? '—'}</TD>
                        <TD>
                          <span
                            data-testid={`on-hand-${item.id}`}
                            className={belowReorder ? 'font-semibold text-red-600 dark:text-red-400' : ''}
                          >
                            {stockUnknown ? '—' : formatQuantity(onHand.toFixed(3))}
                          </span>
                          {item.reorderPoint != null && (
                            <div className="text-xs text-ink-muted">
                              {t('operations.inventory.reorderPoint')}:{' '}
                              {formatQuantity(item.reorderPoint)}
                            </div>
                          )}
                        </TD>
                        <TD>
                          {!stock.length ? (
                            <span className="text-xs text-ink-muted">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {stock.map((row) => (
                                <Badge key={row.warehouseId} tone="neutral">
                                  {row.warehouse?.code ?? row.warehouseId.slice(0, 6)}:{' '}
                                  {formatQuantity(row.onHand)}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </TD>
                        {/*
                          `costPrice` is a cost PER STOCK UNIT at
                          `Decimal(12,4)`, not a total, so `useCurrency()` — which
                          rounds to the currency's minor unit — printed flour at
                          0.0020/g as a flat zero. The 4dp string is shown as the
                          server sends it, with the currency named beside it.
                        */}
                        <TD className="text-end tabular-nums" dir="ltr">
                          {item.costPrice} {currencyCode}
                          <div className="text-xs text-ink-muted">
                            {t('operations.inventory.defs.items.perUnit', {
                              unit: item.unit?.code ?? '',
                            })}
                          </div>
                        </TD>
                        {mayAdjust && (
                          <TD className="text-end">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                aria-label={t('common.edit')}
                                title={t('common.edit')}
                                onClick={() => setItemTarget(item)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                aria-label={t('operations.inventory.defs.items.retire')}
                                title={t('operations.inventory.defs.items.retire')}
                                disabled={busy}
                                onClick={() => retireItem(item)}
                              >
                                <PowerOff className="h-4 w-4" />
                              </Button>
                            </div>
                          </TD>
                        )}
                      </TR>
                    );
                  })
                )}
              </tbody>
            </Table>
          </TableWrap>
        ))}

      {/* ------------------------------------------------------ movements */}
      {tab === 'movements' &&
        (movements.error ? (
          <ErrorState message={movements.error} onRetry={movements.reload} />
        ) : movements.loading && !movementRows.length ? (
          <LoadingState label={t('common.loading')} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('operations.inventory.occurredAt')}</TH>
                  <TH>{t('operations.inventory.item')}</TH>
                  <TH>{t('operations.inventory.movementType')}</TH>
                  <TH>{t('operations.inventory.warehouse')}</TH>
                  <TH className="text-end">{t('common.quantity')}</TH>
                  <TH className="text-end">{t('operations.inventory.totalCost')}</TH>
                  <TH>{t('operations.common.reason')}</TH>
                </TR>
              </THead>
              <tbody>
                {!movementRows.length ? (
                  <TableEmpty colSpan={7}>{t('operations.inventory.noMovements')}</TableEmpty>
                ) : (
                  movementRows.map((movement) => (
                    <TR key={movement.id}>
                      <TD className="whitespace-nowrap">
                        {formatDateTime(movement.occurredAt, i18n.language)}
                      </TD>
                      <TD>{movement.inventoryItem?.name ?? '—'}</TD>
                      <TD>
                        <Badge
                          tone={Number(movement.quantity) < 0 ? 'danger' : 'success'}
                        >
                          {t(`operations.inventory.movementTypes.${movement.type}`)}
                        </Badge>
                      </TD>
                      <TD>{movement.warehouse?.name ?? '—'}</TD>
                      <TD className="text-end" dir="ltr">
                        {formatQuantity(movement.quantity)} {movement.unit?.code ?? ''}
                      </TD>
                      <TD className="text-end">
                        {money(movement.totalCost)}
                      </TD>
                      <TD className="text-xs text-ink-muted">
                        {movement.reason ?? '—'}
                      </TD>
                    </TR>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrap>
        ))}

      {/* ------------------------------------------------------- low stock */}
      {tab === 'low-stock' &&
        (lowStock.error ? (
          <ErrorState message={lowStock.error} onRetry={lowStock.reload} />
        ) : lowStock.loading && !lowStockRows.length ? (
          <LoadingState label={t('common.loading')} />
        ) : !lowStockRows.length ? (
          <EmptyState
            title={t('operations.inventory.noLowStock')}
            hint={t('operations.inventory.noLowStockHint')}
          />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('operations.inventory.item')}</TH>
                  <TH className="text-end">{t('operations.inventory.onHand')}</TH>
                  <TH className="text-end">{t('operations.inventory.reorderPoint')}</TH>
                  <TH className="text-end">{t('operations.inventory.shortfall')}</TH>
                  <TH className="text-end">{t('operations.inventory.suggestedOrder')}</TH>
                </TR>
              </THead>
              <tbody>
                {lowStockRows.map((row) => (
                  <TR key={row.inventoryItemId}>
                    <TD>
                      <div className="font-medium">{row.name}</div>
                      <div className="text-xs text-ink-muted">
                        {row.sku ?? ''} {row.unit?.code ? `· ${row.unit.code}` : ''}
                      </div>
                    </TD>
                    <TD className="text-end">
                      <span className={row.isOutOfStock ? 'font-semibold text-red-600 dark:text-red-400' : ''}>
                        {formatQuantity(row.onHand)}
                      </span>
                      {row.isOutOfStock && (
                        <Badge tone="danger" className="ms-2">
                          {t('operations.inventory.outOfStock')}
                        </Badge>
                      )}
                    </TD>
                    <TD className="text-end">{formatQuantity(row.reorderPoint)}</TD>
                    <TD className="text-end">{formatQuantity(row.shortfall)}</TD>
                    <TD className="text-end">{formatQuantity(row.suggestedOrderQuantity)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        ))}

      {/* ------------------------------------------------------- valuation */}
      {tab === 'valuation' &&
        (valuation.error ? (
          <ErrorState message={valuation.error} onRetry={valuation.reload} />
        ) : valuation.loading && !valuation.data ? (
          <LoadingState label={t('common.loading')} />
        ) : (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('operations.inventory.tabs.valuation')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-8">
                <div>
                  <div className="text-xs text-ink-muted">
                    {t('operations.inventory.totalValue')}
                  </div>
                  <div className="text-2xl font-semibold">
                    {money(valuation.data?.totalValue)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-ink-muted">
                    {t('operations.inventory.valuedLines')}
                  </div>
                  <div className="text-2xl font-semibold">{valuation.data?.lines ?? 0}</div>
                </div>
              </CardContent>
            </Card>

            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('operations.inventory.item')}</TH>
                    <TH>{t('operations.inventory.warehouse')}</TH>
                    <TH className="text-end">{t('operations.inventory.onHand')}</TH>
                    <TH className="text-end">{t('operations.inventory.value')}</TH>
                  </TR>
                </THead>
                <tbody>
                  {stockUnknown ? (
                    <TableEmpty colSpan={4}>
                      {t('operations.inventory.stockUnknown')}
                    </TableEmpty>
                  ) : !levelRows.length ? (
                    <TableEmpty colSpan={4}>{t('operations.inventory.noStock')}</TableEmpty>
                  ) : (
                    levelRows.map((row) => (
                      <TR key={`${row.inventoryItemId}:${row.warehouseId}`}>
                        <TD>{row.inventoryItem?.name ?? '—'}</TD>
                        <TD>{row.warehouse?.name ?? '—'}</TD>
                        <TD className="text-end">
                          {formatQuantity(row.onHand)} {row.unit?.code ?? ''}
                        </TD>
                        <TD className="text-end">{money(row.value)}</TD>
                      </TR>
                    ))
                  )}
                </tbody>
              </Table>
            </TableWrap>
          </div>
        ))}

      {/* -------------------------------------------------------- variance */}
      {tab === 'margin' &&
        (margin.error ? (
          <ErrorState message={margin.error} onRetry={margin.reload} />
        ) : margin.loading && !margin.data ? (
          <LoadingState label={t('common.loading')} />
        ) : !margin.data?.items.length ? (
          <EmptyState
            title={t('operations.inventory.noMargin')}
            hint={t('operations.inventory.noMarginHint')}
          />
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: t('operations.inventory.revenue'), value: money(margin.data.totals.revenue) },
                { label: t('operations.inventory.cost'), value: money(margin.data.totals.cost) },
                { label: t('operations.inventory.margin'), value: money(margin.data.totals.margin) },
                {
                  label: t('operations.inventory.marginPercent'),
                  value:
                    margin.data.totals.marginPercent === null
                      ? '—'
                      : `${Number(margin.data.totals.marginPercent).toFixed(1)}%`,
                },
              ].map((tile) => (
                <Card key={tile.label}>
                  <CardContent className="p-4">
                    <p className="text-xs text-ink-muted">{tile.label}</p>
                    <p className="mt-1 text-xl font-semibold tabular-nums">{tile.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('operations.inventory.item')}</TH>
                    <TH className="text-end">{t('operations.inventory.quantitySold')}</TH>
                    <TH className="text-end">{t('operations.inventory.revenue')}</TH>
                    <TH className="text-end">{t('operations.inventory.cost')}</TH>
                    <TH className="text-end">{t('operations.inventory.margin')}</TH>
                    <TH className="text-end">{t('operations.inventory.marginPercent')}</TH>
                    <TH className="text-end">{t('operations.inventory.costed')}</TH>
                  </TR>
                </THead>
                <tbody>
                  {margin.data.items.map((row) => {
                    // A line with no stock movement has no cost, so its margin
                    // reads as pure profit. Say how many lines are actually
                    // costed rather than letting the number imply all of them.
                    const partial = row.costedOrderItems < row.orderItems;
                    return (
                      <TR key={row.catalogItemId}>
                        <TD>{row.name}</TD>
                        <TD className="text-end tabular-nums">{row.quantitySold}</TD>
                        <TD className="text-end tabular-nums">{money(row.revenue)}</TD>
                        <TD className="text-end tabular-nums">{money(row.cost)}</TD>
                        <TD className="text-end tabular-nums">{money(row.margin)}</TD>
                        <TD className="text-end tabular-nums">
                          {row.marginPercent === null
                            ? '—'
                            : `${Number(row.marginPercent).toFixed(1)}%`}
                        </TD>
                        <TD className="text-end tabular-nums">
                          {partial ? (
                            <Badge tone="warning">
                              {row.costedOrderItems}/{row.orderItems}
                            </Badge>
                          ) : (
                            <span className="text-ink-muted">
                              {row.costedOrderItems}/{row.orderItems}
                            </span>
                          )}
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          </div>
        ))}

      {tab === 'variance' &&
        (variance.error ? (
          <ErrorState message={variance.error} onRetry={variance.reload} />
        ) : variance.loading && !varianceRows.length ? (
          <LoadingState label={t('common.loading')} />
        ) : !varianceRows.length ? (
          <EmptyState
            title={t('operations.inventory.noVariance')}
            hint={t('operations.inventory.noVarianceHint')}
          />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('operations.inventory.occurredAt')}</TH>
                  <TH>{t('operations.inventory.item')}</TH>
                  <TH>{t('operations.inventory.warehouse')}</TH>
                  <TH className="text-end">{t('operations.inventory.expected')}</TH>
                  <TH className="text-end">{t('operations.inventory.counted')}</TH>
                  <TH className="text-end">{t('operations.inventory.variance')}</TH>
                  <TH className="text-end">{t('operations.inventory.varianceValue')}</TH>
                </TR>
              </THead>
              <tbody>
                {varianceRows.map((row) => {
                  const drift = Number(row.variance);
                  return (
                    <TR key={row.movementId}>
                      <TD className="whitespace-nowrap">
                        {formatDateTime(row.occurredAt, i18n.language)}
                      </TD>
                      <TD>{row.inventoryItem?.name ?? '—'}</TD>
                      <TD>{row.warehouse?.name ?? '—'}</TD>
                      <TD className="text-end">{formatQuantity(row.expected)}</TD>
                      <TD className="text-end">{formatQuantity(row.counted)}</TD>
                      <TD className="text-end">
                        <span
                          className={
                            drift < 0
                              ? 'font-semibold text-red-600 dark:text-red-400'
                              : drift > 0
                                ? 'font-semibold text-green-700 dark:text-green-400'
                                : ''
                          }
                          dir="ltr"
                        >
                          {formatQuantity(row.variance)}
                        </span>
                      </TD>
                      <TD className="text-end">
                        {money(row.varianceValue)}
                      </TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        ))}

      {/* ------------------------------------------- stock operation dialog */}
      <Dialog
        open={!!operation}
        onClose={() => setOperation(null)}
        title={operation ? t(`operations.inventory.actions.${operation}`) : ''}
        description={t('operations.inventory.decimalHint')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOperation(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={submitOperation}
              disabled={busy || !!operationError}
              title={operationError ?? undefined}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field
            label={t('operations.inventory.item')}
            required
            error={form.inventoryItemId ? undefined : t('operations.inventory.itemRequired')}
          >
            <Select
              value={form.inventoryItemId}
              onChange={(e) => setForm({ ...form, inventoryItemId: e.target.value })}
            >
              {itemOptions}
            </Select>
          </Field>

          <Field
            label={t('common.quantity')}
            required
            error={form.quantity.trim() ? (quantityError ?? undefined) : undefined}
            hint={
              operation === 'adjust'
                ? t('operations.inventory.signedHint')
                : t('operations.inventory.quantityHint')
            }
          >
            <Input
              inputMode="decimal"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              placeholder="2.500"
            />
          </Field>

          <Field label={t('operations.inventory.unit')} hint={t('operations.inventory.unitHint')}>
            <Select
              value={form.unitId}
              onChange={(e) => setForm({ ...form, unitId: e.target.value })}
            >
              <option value="">{t('operations.inventory.stockUnit')}</option>
              {unitRows.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name} ({unit.code})
                </option>
              ))}
            </Select>
          </Field>

          {/* A transfer must name its source: the API resolves an omitted one
              to the branch default and then refuses a move to itself, which the
              client cannot predict without repeating that resolution. */}
          <Field
            label={t('operations.inventory.warehouse')}
            required={operation === 'transfer'}
            error={
              operation === 'transfer' && !form.warehouseId
                ? t('operations.inventory.sourceRequired')
                : undefined
            }
            hint={t('operations.inventory.warehouseHint')}
          >
            <Select
              value={form.warehouseId}
              onChange={(e) => setForm({ ...form, warehouseId: e.target.value })}
            >
              <option value="">{t('operations.inventory.defaultWarehouse')}</option>
              {warehouseRows.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>
          </Field>

          {operation === 'transfer' && (
            <Field
              label={t('operations.inventory.destination')}
              required
              error={
                form.toWarehouseId ? undefined : t('operations.inventory.destinationRequired')
              }
            >
              <Select
                value={form.toWarehouseId}
                onChange={(e) => setForm({ ...form, toWarehouseId: e.target.value })}
              >
                <option value="">{t('operations.filters.choose')}</option>
                {warehouseRows
                  .filter((w) => w.id !== form.warehouseId)
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
              </Select>
            </Field>
          )}

          {operation === 'receive' && (
            <Field
              label={t('operations.inventory.unitCost')}
              hint={t('operations.inventory.unitCostHint')}
            >
              <Input
                inputMode="decimal"
                value={form.unitCost}
                onChange={(e) => setForm({ ...form, unitCost: e.target.value })}
                placeholder="48.5000"
              />
            </Field>
          )}

          <Field label={t('operations.common.reason')} required={needsReason}>
            <Textarea
              rows={2}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </Field>
        </div>
      </Dialog>

      {/* ------------------------------------------- transfer dead-end dialog */}
      {/*
        X-16 — "needs two warehouses" used to be a tooltip on a dead button.
        The precondition is real, so this does not pretend to lift it; it hands
        over the two things that actually satisfy it, which no client could do
        before: make another store, or bring a retired one back.
      */}
      <Dialog
        open={transferDeadEnd}
        onClose={() => setTransferDeadEnd(false)}
        title={t('operations.inventory.defs.transferBlocked.title')}
        description={t('operations.inventory.defs.transferBlocked.body', {
          count: warehouseRows.length,
        })}
        className="max-w-xl"
        footer={
          <Button variant="ghost" size="lg" onClick={() => setTransferDeadEnd(false)}>
            {t('common.close')}
          </Button>
        }
      >
        <div className="space-y-3">
          {retiredWarehouses.map((warehouse) => (
            <div
              key={warehouse.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
            >
              <div>
                <p className="font-medium">{warehouse.name}</p>
                <p className="text-xs text-ink-muted">
                  {warehouse.code} · {t('operations.inventory.defs.inactive')}
                </p>
              </div>
              <Button
                size="lg"
                onClick={() => {
                  setTransferDeadEnd(false);
                  select('warehouses');
                }}
              >
                {t('operations.inventory.defs.warehouses.reactivate')}
              </Button>
            </div>
          ))}

          {mayAdjust ? (
            <Button
              size="lg"
              variant={retiredWarehouses.length ? 'outline' : 'default'}
              className="w-full"
              onClick={() => {
                setTransferDeadEnd(false);
                select('warehouses');
                setWarehouseTarget('new');
              }}
            >
              <Plus className="h-4 w-4" />
              {t('operations.inventory.defs.warehouses.new')}
            </Button>
          ) : (
            // `inventory.transfer` without `inventory.adjust` is a real grant:
            // a stock controller who may move stock but not define where it
            // lives. Telling them to make a warehouse would be telling them to
            // earn a 403.
            <p className="text-sm text-ink-muted">
              {t('operations.inventory.defs.transferBlocked.needsAdjust')}
            </p>
          )}
        </div>
      </Dialog>

      <UnitDialog
        target={unitTarget}
        units={unitRows}
        onClose={() => setUnitTarget(null)}
        onSaved={() => {
          setUnitTarget(null);
          units.reload();
          // An item's unit label is denormalised into the items list, so a
          // renamed unit would otherwise keep its old code on that tab.
          items.reload();
        }}
      />

      <InventoryItemDialog
        target={itemTarget}
        units={unitRows}
        onClose={() => setItemTarget(null)}
        onSaved={() => {
          setItemTarget(null);
          items.reload();
          levels.reload();
        }}
      />

      <WarehouseDialog
        target={warehouseTarget}
        branches={branches}
        onClose={() => setWarehouseTarget(null)}
        onSaved={() => {
          setWarehouseTarget(null);
          warehouses.reload();
        }}
      />
    </div>
  );
}
